import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import {
  PERMISSIONS,
  formatMinutes,
  type AircraftComponent,
  type ComponentForecast,
  type ComponentStatus,
  type MaintenanceOverview,
  type MaintenanceRecord,
} from '@jupi/shared'
import { ConfirmDialog, DISPLAY_STYLE, MaintenanceBar, NavDataCard, RemoteContent, api, formatDate, useAuth, useSnackbar } from '@jupi/ui'
import { HUOLTO, useHasGps, useOverview } from '../api'
import { EquipmentDialog } from '../components/EquipmentDialog'
import { FittingParts } from '../components/FittingParts'
import { ZONE_COLOUR, ZoneBadge } from '../components/ZoneBadge'

const dateLabel = (date: string) => formatDate(`${date}T12:00:00Z`)
const HISTORY_SHORT = 3

// One aircraft on one screen, top to bottom in the order a mechanic asks the
// questions: what are the hours, when is the next one due, what was done last,
// how is the engine. The one thing to do -- perform maintenance -- is a single
// huge button pinned to the bottom of the screen.
export default function AircraftPage() {
  const { registration = '' } = useParams()
  const { data, isLoading, error } = useOverview(registration)

  return (
    <RemoteContent isLoading={isLoading} error={error}>
      {data ? <Overview overview={data} /> : null}
    </RemoteContent>
  )
}

function Overview({ overview: o }: { overview: MaintenanceOverview }) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState<{ component: AircraftComponent | null } | null>(null)
  const [showAll, setShowAll] = useState(false)
  const { can } = useAuth()
  const last = o.records[0]
  const { data: hasGps = false } = useHasGps(o.registration)
  const engine = o.components.find((c) => c.kind === 'engine')

  return (
    <Stack spacing={2.5} sx={{ pb: 12 }}>
      <Box>
        <Typography sx={{ ...DISPLAY_STYLE, fontSize: '3rem', lineHeight: 1 }}>{o.registration}</Typography>
        <Typography color="text.secondary">{o.aircraftType ?? ''}</Typography>
      </Box>

      <NavDataCard registration={o.registration} hasGps={hasGps} canEdit={can(PERMISSIONS.maintenanceWrite)} />

      <Section title={t('huolto.airframeHours')}>
        {o.airframeMinutes != null ? (
          <Typography sx={{ fontSize: '2.75rem', fontWeight: 700, lineHeight: 1.1 }}>
            {formatMinutes(o.airframeMinutes)} <Typography component="span" variant="h5">h</Typography>
          </Typography>
        ) : (
          <Alert severity="warning">
            <strong>{t('huolto.noAirframe')}.</strong> {t('huolto.noAirframeHelp')}
          </Alert>
        )}
        {engine?.status ? (
          <Typography sx={{ mt: 0.5 }} color="text.secondary">
            {t('huolto.engineSince', { time: formatMinutes(engine.status.minutesSince) })}
          </Typography>
        ) : null}
      </Section>

      <Section title={t('huolto.nextMaintenance')}>
        {o.schedule ? (
          <Stack spacing={1.5}>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {o.schedule.nextType} {t('huolto.at', { at: formatMinutes(o.schedule.nextDueMinutes) })}
              </Typography>
              {o.status ? <ZoneBadge zone={o.status.zone} minutesToDue={o.status.minutesToDue} /> : null}
            </Stack>
            {o.schedule.nextDueDate ? (
              <Typography color={o.status?.daysToDue != null && o.status.daysToDue < 0 ? 'error' : 'text.secondary'}>
                {t('huolto.dueOn', { date: dateLabel(o.schedule.nextDueDate) })}
              </Typography>
            ) : null}
            {o.status ? <MaintenanceBar schedule={o.schedule} status={o.status} /> : null}
          </Stack>
        ) : (
          <Typography color="text.secondary">{t('huolto.noSchedule')}</Typography>
        )}
      </Section>

      <Section title={t('huolto.lastMaintenance')}>
        {last ? <RecordLine record={last} /> : <Typography color="text.secondary">{t('huolto.noneYet')}</Typography>}
      </Section>

      <Section title={t('huolto.equipment')} hint={t('huolto.equipmentHint')}>
        {o.components.length === 0 ? <Typography color="text.secondary">{t('huolto.noEquipment')}</Typography> : null}
        <List disablePadding>
          {o.components.map((c) => (
            <PartRow
              key={c.componentId}
              registration={o.registration}
              part={c}
              canComplete={o.airframeMinutes != null}
              airframeMinutes={o.airframeMinutes}
              onEdit={() => setEditing({ component: c })}
            />
          ))}
        </List>
        <Button variant="outlined" startIcon={<Icon icon="mdi:plus" />} onClick={() => setEditing({ component: null })} sx={{ mt: 2 }}>
          {t('huolto.addEquipment')}
        </Button>
      </Section>

      {can(PERMISSIONS.inventoryRead, PERMISSIONS.inventoryManage) ? (
        <Section title={t('huolto.partsFitting')} hint={t('huolto.partsFittingHint', { registration: o.registration })}>
          <FittingParts registration={o.registration} />
        </Section>
      ) : null}

      {o.records.length > 1 ? (
        <Section title={t('huolto.history')}>
          <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />} spacing={1.5}>
            {(showAll ? o.records : o.records.slice(0, HISTORY_SHORT)).map((r) => (
              <RecordLine key={r.recordId} record={r} />
            ))}
          </Stack>
          {o.records.length > HISTORY_SHORT ? (
            <Button onClick={() => setShowAll((v) => !v)} sx={{ mt: 1 }}>
              {showAll ? t('huolto.showLess') : t('huolto.showAll')}
            </Button>
          ) : null}
        </Section>
      ) : null}

      {/* The one thing to do. Pinned, full width, 72 px tall. */}
      <Box
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          p: 2,
          pb: 'calc(16px + env(safe-area-inset-bottom))',
          bgcolor: 'background.default',
          borderTop: 1,
          borderColor: 'divider',
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <Box sx={{ maxWidth: 'md', mx: 'auto' }}>
          <Button
            component={RouterLink}
            to="perform"
            variant="contained"
            size="large"
            fullWidth
            disabled={o.airframeMinutes == null}
            startIcon={<Icon icon="mdi:wrench-check" width={30} />}
          >
            {t('huolto.perform')}
          </Button>
        </Box>
      </Box>

      {editing ? (
        <EquipmentDialog
          registration={o.registration}
          airframeMinutes={o.airframeMinutes}
          component={editing.component}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </Stack>
  )
}

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
      <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.95rem', letterSpacing: '0.08em' }}>
        {title}
      </Typography>
      {hint ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {hint}
        </Typography>
      ) : null}
      {children}
    </Paper>
  )
}

function RecordLine({ record: r }: { record: MaintenanceRecord }) {
  const { t } = useTranslation()
  return (
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: '1.2rem' }}>
        {r.maintenanceType} · {dateLabel(r.performedOn)} · {t('huolto.at', { at: formatMinutes(r.airframeMinutes) })}
      </Typography>
      <Typography color="text.secondary">
        {[r.performedBy ?? r.recordedByName, r.notes].filter(Boolean).join(' · ')}
      </Typography>
    </Box>
  )
}

// "812:30 h" since last done, and below it how long until it is due: coloured
// and worded, so it reads without the colour.
function PartHours({ status }: { status: ComponentStatus | null }) {
  const { t } = useTranslation()
  if (!status) return null
  return (
    <Stack sx={{ alignItems: 'flex-end', ml: 1 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '1.2rem' }}>{t('huolto.sinceDone', { time: formatMinutes(status.minutesSince) })}</Typography>
      {status.minutesToDue != null && status.zone ? (
        <Chip
          size="small"
          variant="outlined"
          label={
            status.minutesToDue >= 0
              ? t('huolto.dueIn', { time: formatMinutes(status.minutesToDue) })
              : t('huolto.pastDue', { time: formatMinutes(-status.minutesToDue) })
          }
          sx={{ color: ZONE_COLOUR[status.zone], borderColor: ZONE_COLOUR[status.zone], mt: 0.5 }}
        />
      ) : null}
    </Stack>
  )
}

// One tracked part: tap the row to edit, "Done" to reset it on its own (no
// maintenance record) at the airframe hours now.
function PartRow({
  registration,
  part: c,
  canComplete,
  airframeMinutes,
  onEdit,
}: {
  registration: string
  part: AircraftComponent & { status: ComponentStatus | null; forecast: ComponentForecast | null }
  canComplete: boolean
  airframeMinutes: number | null
  onEdit: () => void
}) {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const [confirming, setConfirming] = useState(false)
  const complete = useMutation({
    mutationFn: () => api.post(`/api/maintenance/${encodeURIComponent(registration)}/components/${c.componentId}/complete`, {}),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: HUOLTO })
      showSnackbar(t('huolto.markedDone'))
    },
    onError: () => showSnackbar(t('huolto.saveFailed'), { severity: 'error' }),
  })
  const forecastDate = c.forecast?.outcome.kind === 'date' ? c.forecast.outcome.date : null

  return (
    <Stack direction="row" sx={{ alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
      <ListItemButton onClick={onEdit} sx={{ px: 0, flex: 1 }}>
        <ListItemText
          primary={c.name}
          secondary={
            <>
              {t(`huolto.kind_${c.kind}`)}
              {c.isTbo ? ` · ${t('huolto.tboBadge')}` : ''}
              {c.intervalHours ? ` · ${t('huolto.everyHours', { hours: c.intervalHours })}` : ''}
              {forecastDate ? ` · ${t('huolto.forecastOn', { date: dateLabel(forecastDate) })}` : ''}
              {c.forecast?.outcome.kind === 'exceeded' ? ` · ${t('huolto.forecastExceeded')}` : ''}
            </>
          }
          slotProps={{ primary: { sx: { fontWeight: 700, fontSize: '1.2rem' } } }}
        />
        <PartHours status={c.status} />
        <Icon icon="mdi:pencil-outline" width={24} style={{ marginLeft: 12, opacity: 0.6 }} />
      </ListItemButton>
      <Button
        size="small"
        variant="outlined"
        startIcon={<Icon icon="mdi:check" />}
        disabled={!canComplete || complete.isPending}
        onClick={() => setConfirming(true)}
        sx={{ ml: 1, flexShrink: 0 }}
      >
        {t('huolto.markDone')}
      </Button>
      <ConfirmDialog
        open={confirming}
        title={t('huolto.markDoneTitle')}
        message={t('huolto.markDoneConfirm', { name: c.name, time: formatMinutes(airframeMinutes ?? 0) })}
        confirmLabel={t('huolto.markDone')}
        cancelLabel={t('huolto.cancel')}
        onConfirm={() => {
          setConfirming(false)
          complete.mutate()
        }}
        onClose={() => setConfirming(false)}
      />
    </Stack>
  )
}
